import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJumble } from '../utils/api';
import SeoContent from '../components/SeoContent';
import DefinableWord from '../components/DefinableWord';
import PageSEO from '../components/PageSEO';

export default function JumbleSolver() {
  const [letters, setLetters] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [minLen, setMinLen]   = useState(3);

  async function handleSolve(e) {
    e.preventDefault();
    if (!letters.trim()) return;
    setLoading(true); setError(''); setResults(null);
    try {
      const { data } = await apiJumble(letters);
      const filtered = data.results.filter(r => r.length >= minLen);
      setResults(filtered);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Is the server running?');
    } finally { setLoading(false); }
  }

  const grouped = results
    ? results.reduce((acc, r) => { (acc[r.length] = acc[r.length] || []).push(r); return acc; }, {})
    : {};

  return (
    <div className="game-page">
      <PageSEO page="jumble" />
      <div className="container" style={{maxWidth:700}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🔀</span>
          <h1>Jumble Solver</h1>
          <p>Enter any letters and find every valid word you can make from them!</p>
        </div>

        <div className="solver-card">
          <form onSubmit={handleSolve}>
            <div className="mb-3">
              <label className="fw-bold mb-1">Enter Letters</label>
              <input
                className="big-input"
                value={letters}
                onChange={e => setLetters(e.target.value.replace(/[^a-zA-Z]/g, ''))}
                placeholder="e.g. RETAW"
                maxLength={15}
                autoFocus
              />
              <div className="d-flex align-items-center gap-3 mt-2">
                <small className="text-muted fw-bold">{letters.length} letter{letters.length !== 1 ? 's' : ''}</small>
                <div className="d-flex align-items-center gap-2">
                  <small className="text-muted fw-bold">Min word length:</small>
                  {[2,3,4,5].map(n => (
                    <button
                      key={n} type="button"
                      onClick={() => setMinLen(n)}
                      className="btn btn-sm fw-bold"
                      style={{
                        borderRadius:'50px', width:'32px', height:'32px', padding:0,
                        background: minLen === n ? 'var(--primary)' : 'var(--border)',
                        color: minLen === n ? '#fff' : 'var(--text)',
                        border:'none',
                      }}
                    >{n}</button>
                  ))}
                </div>
              </div>
            </div>
            <button type="submit" className="solve-btn" disabled={loading || !letters}>
              {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Solving…</> : '🔀 Find Words'}
            </button>
          </form>
        </div>

        {error && <div className="alert alert-danger alert-custom">{error}</div>}

        {results !== null && (
          <div className="solver-card">
            <p className="result-count">
              Found <strong>{results.length}</strong> word{results.length !== 1 ? 's' : ''} from <strong>{letters.toUpperCase()}</strong>
            </p>
            {results.length === 0 ? (
              <p className="text-muted">No words found. Try more letters!</p>
            ) : (
              Object.keys(grouped).sort((a,b) => b-a).map(len => (
                <div key={len} className="mb-3">
                  <h6 className="fw-bold text-muted mb-2">{len}-letter words ({grouped[len].length})</h6>
                  <div className="words-cloud">
                    {grouped[len].map(r => (
                      <DefinableWord key={r.word} word={r.word} className="result-badge" style={{textDecoration:'none'}}>
                        {r.word.toUpperCase()}
                      </DefinableWord>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      <SeoContent game="jumble" />
      </div>
    </div>
  );
}
